import { Link } from '@tanstack/react-router';
import { useFormContext } from 'react-hook-form';
import {
  buildDashboardDataTableColumns,
  DashboardDataTable,
  DashboardDataTableColumnKind,
  DashboardPageFormBody,
  DashboardTableStatusTone,
  dashboardTableTextActionClassName,
  useDashboardDataTableClientPage,
} from '@shigo-ui/dashboard-ui';

import { dashboardT } from '../../../../../lib/messages';
import { DemandPartnerStatus } from '../../../partners/model';
import type { OwnerFormValue } from '../../formSchema';
import type { OwnerDemandPartner } from '../../model';

const columns = buildDashboardDataTableColumns<OwnerDemandPartner>([
  {
    id: 'name',
    kind: DashboardDataTableColumnKind.Text,
    header: dashboardT('owners.partners.name'),
    headerClassName: 'w-64 pl-4',
    className: 'w-64 pl-4 font-medium',
    value: (partner) => (
      <Link
        className={dashboardTableTextActionClassName()}
        to="/demand/partners/$demandPartnerId/details/overview"
        params={{ demandPartnerId: partner.id }}
      >
        {partner.displayName}
      </Link>
    ),
  },
  textColumn('bidderCode', 'owners.partners.bidder', (partner) => partner.bidderCode ?? '-'),
  textColumn('integrations', 'owners.partners.integrations', (partner) =>
    String(partner.demandIntegrationCnt ?? partner.demandIntegrationsCnt ?? 0),
  ),
  {
    id: 'status',
    kind: DashboardDataTableColumnKind.Status,
    header: dashboardT('owners.partners.status'),
    headerClassName: 'w-40',
    className: 'w-40',
    label: (partner) => partner.status ?? '-',
    tone: (partner) => demandPartnerStatusTone(partner.status),
  },
]);

export function OwnerPartnersFields() {
  const { getValues } = useFormContext<OwnerFormValue>();
  const demandPartners = getValues('demandPartners');
  const connectedDemandPartners = getValues('connectedDemandPartners');
  const partners = demandPartners.length ? demandPartners : connectedDemandPartners;

  // Long lists render one page of rows, not every row (keeps tab switches fast).
  const tablePage = useDashboardDataTableClientPage(partners);
  return (
    <DashboardPageFormBody
      width="lg"
      title={dashboardT('owners.tabs.partners')}
      description={dashboardT('owners.partners.description')}
    >
      <DashboardDataTable
        tableLabel={dashboardT('owners.partners.tableLabel')}
        columns={columns}
        items={tablePage.items}
        pagination={tablePage.pagination}
        getRowKey={(partner) => partner.id}
        emptyMessage={dashboardT('owners.partners.empty')}
        minWidthClassName="min-w-[720px]"
        surfaceClassName="shadow-none"
      />
    </DashboardPageFormBody>
  );
}

function textColumn(
  id: string,
  key: 'owners.partners.bidder' | 'owners.partners.integrations',
  value: (partner: OwnerDemandPartner) => React.ReactNode,
) {
  return {
    id,
    kind: DashboardDataTableColumnKind.Text,
    header: dashboardT(key),
    headerClassName: 'w-40',
    className: 'w-40 truncate',
    value,
  } as const;
}

function demandPartnerStatusTone(status?: string | null) {
  if (status === DemandPartnerStatus.Active) return DashboardTableStatusTone.Success;
  if (status === DemandPartnerStatus.Inactive) return DashboardTableStatusTone.Warning;
  return DashboardTableStatusTone.Neutral;
}
